import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export default function EmployeesPanel() {
  const [employees, setEmployees] = useState([]);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [resetId, setResetId] = useState(null);
  const [resetPin, setResetPin] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/employees");
      setEmployees(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!name.trim() || !/^\d{4}$/.test(pin)) {
      toast.error("Name and a 4-digit PIN are required");
      return;
    }
    try {
      await api.post("/admin/employees", { name: name.trim(), pin });
      toast.success(`${name.trim()} can now clock in with PIN ${pin}`);
      setName("");
      setPin("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this employee? Their time entries stay on record.")) return;
    await api.delete(`/admin/employees/${id}`).catch((e) => toast.error(apiError(e)));
    load();
  };

  const toggleActive = async (emp) => {
    await api.patch(`/admin/employees/${emp.id}`, { active: !emp.active }).catch(() => {});
    load();
  };

  const saveResetPin = async () => {
    if (!/^\d{4}$/.test(resetPin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    await api.patch(`/admin/employees/${resetId}`, { pin: resetPin }).catch((e) => toast.error(apiError(e)));
    toast.success("PIN updated");
    setResetId(null);
    setResetPin("");
  };

  return (
    <div>
      <div className="rounded-3xl border border-leafline bg-cream p-5">
        <h3 className="font-display text-lg font-semibold text-deep">Add a team member</h3>
        <div className="mt-3 flex flex-wrap gap-3">
          <input
            data-testid="employee-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="First name"
            className="w-44 rounded-2xl border-2 border-chalk/10 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
          />
          <input
            data-testid="employee-pin-input"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="4-digit PIN"
            inputMode="numeric"
            className="w-32 rounded-2xl border-2 border-chalk/10 bg-white px-4 py-2.5 text-sm font-medium outline-none focus:border-artichoke"
          />
          <button
            data-testid="employee-add-button"
            onClick={add}
            className="flex items-center gap-1.5 rounded-full bg-artichoke px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-deep"
          >
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
        <p className="mt-2 text-[11px] text-chalk/45">
          They'll tap their name + enter this PIN on the Time Clock screen (/clock) — great on a shared tablet.
        </p>
      </div>

      <div className="mt-6 space-y-2.5">
        {employees.length === 0 && (
          <p data-testid="employees-empty" className="py-10 text-center text-sm text-chalk/50">
            No team members yet — add your first one above.
          </p>
        )}
        {employees.map((emp) => (
          <div
            key={emp.id}
            data-testid={`employee-row-${emp.id}`}
            className={`flex flex-wrap items-center gap-3 rounded-2xl border border-leafline bg-cream p-4 ${!emp.active ? "opacity-50" : ""}`}
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-artichoke/15 font-display text-lg font-bold text-artichoke">
              {emp.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">{emp.name}</p>
              <p className="text-xs text-chalk/45">PIN •••• · {emp.active ? "Active" : "Inactive"}</p>
            </div>
            {resetId === emp.id ? (
              <div className="flex items-center gap-2">
                <input
                  data-testid={`employee-reset-pin-input-${emp.id}`}
                  value={resetPin}
                  onChange={(e) => setResetPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="New PIN"
                  inputMode="numeric"
                  className="w-24 rounded-xl border-2 border-artichoke bg-white px-3 py-1.5 text-sm font-medium outline-none"
                />
                <button data-testid={`employee-reset-save-${emp.id}`} onClick={saveResetPin} className="rounded-full bg-artichoke px-4 py-1.5 text-xs font-bold text-cream">
                  Save
                </button>
                <button onClick={() => setResetId(null)} className="text-xs font-bold text-chalk/50">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                data-testid={`employee-reset-pin-${emp.id}`}
                onClick={() => setResetId(emp.id)}
                className="rounded-full border-2 border-chalk/10 px-4 py-1.5 text-xs font-bold text-chalk/60 hover:border-artichoke hover:text-artichoke"
              >
                Reset PIN
              </button>
            )}
            <button
              data-testid={`employee-toggle-${emp.id}`}
              onClick={() => toggleActive(emp)}
              className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wide ${
                emp.active ? "bg-artichoke/15 text-artichoke" : "bg-chalk/10 text-chalk/50"
              }`}
            >
              {emp.active ? "Active" : "Inactive"}
            </button>
            <button data-testid={`employee-delete-${emp.id}`} onClick={() => remove(emp.id)} className="rounded-full border border-chalk/10 p-2 text-chalk/60 hover:border-red-300 hover:text-red-600" aria-label="Delete employee">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
